import React from 'react';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';

interface Column {
  key: string;
  title?: string;
  label?: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  className?: string;
  render?: (value: any, item: any) => React.ReactNode;
}

interface TableProps {
  columns?: Column[];
  data?: any[];
  isLoading?: boolean;
  onRowClick?: (item: any) => void;
  renderCell?: (item: any, key: string) => React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyMessage?: string;
}

export const Table = ({
  columns,
  data,
  isLoading,
  onRowClick,
  renderCell,
  children,
  className = '',
  emptyTitle = 'Không có dữ liệu',
  emptyDescription = 'Hiện chưa có dữ liệu để hiển thị trong danh sách này.',
  emptyMessage
}: TableProps) => {
  if (children) {
    return (
      <div className="w-full overflow-x-auto border border-slate-100 rounded-md shadow-sm bg-white">
        <table className={`w-full text-left border-collapse bg-white ${className}`}>
          {children}
        </table>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto border border-slate-100 rounded-md shadow-sm bg-white">
      <table className={`w-full text-left border-collapse ${className}`}>
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            {columns?.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={`px-4 py-2.5 sm:text-sm text-xs font-normal text-slate-500 tracking-wider ${
                  (col.headerAlign || col.align || 'left') === 'center'
                    ? 'text-center'
                    : (col.headerAlign || col.align || 'left') === 'right'
                      ? 'text-right'
                      : 'text-left'
                } ${col.className || ''}`}
              >
                {col.title || col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {columns?.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : data && data.length > 0 ? (
            data.map((item, idx) => (
              <tr
                key={item.id || idx}
                onClick={() => onRowClick?.(item)}
                className={`hover:bg-slate-50/50 transition-colors group ${onRowClick ? 'cursor-pointer' : ''}`}
              >
                {columns?.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 sm:text-sm text-xs ${
                      col.align === 'center'
                        ? 'text-center'
                        : col.align === 'right'
                          ? 'text-right'
                          : 'text-left'
                    } ${col.className || ''}`}
                  >
                    {col.render
                      ? col.render(item[col.key], item)
                      : renderCell
                        ? renderCell(item, col.key)
                        : item[col.key] !== undefined
                          ? String(item[col.key])
                          : '-'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns?.length || 1} className="py-12">
                <EmptyState title={emptyTitle} description={emptyMessage ?? emptyDescription} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const TableHeader = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <thead className={`bg-slate-50 border-b border-slate-100 ${className}`}>
    {children}
  </thead>
);

export const TableRow = ({ children, className = '', ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${className}`} {...props}>
    {children}
  </tr>
);

export const TableHead = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <th className={`px-4 py-2.5 sm:text-sm text-xs font-normal text-slate-500 tracking-wider ${className}`}>
    {children}
  </th>
);

export const TableCell = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <td className={`px-4 py-3 sm:text-sm text-xs text-slate-700 ${className}`}>
    {children}
  </td>
);

export const TableBody = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <tbody className={className}>
    {children}
  </tbody>
);
